/**
 * caseStudyRepo.js — merged repository for CloudLab case studies.
 *
 * Phase 4.0.1 abstraction. Public components should import from here
 * instead of reaching directly into `caseStudies.js` so the two data
 * sources below are treated uniformly:
 *
 *   1. CODE-MANAGED  — static records in `src/data/caseStudies.js`
 *                       (UniCRM.ai, TeachingSoftware.ai, Nexus One draft).
 *                       These keep working EXACTLY as before — never fetched.
 *   2. ADMIN-MANAGED — rows in the backend `projects` collection where
 *                       `publish_as_case_study === true` AND
 *                       `publication_status === "PUBLISHED"`.
 *                       Surfaced via `GET /api/case-studies[/{slug}]`.
 *
 * Code-managed records WIN on slug collision (defensive — ensures a future
 * accidental DB record with slug "unicrm-ai" cannot overwrite the hand-
 * written production page).
 *
 * All public case-study consumers (CaseStudies.jsx, CaseStudyDetail.jsx,
 * RelatedCaseStudies.jsx, MetricsBlock.jsx) call hooks from this file.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  CASE_STUDIES,
  getPublishedCaseStudies,
  getCaseStudyBySlug as getCodeManagedBySlug,
  findCaseStudies as findCodeManaged,
} from "@/data/caseStudies";
import { getMetricById } from "@/data/projectMetrics";

const CODE_SLUGS = new Set(CASE_STUDIES.map((c) => c.slug));

/* ------------------------------------------------------------------ */
/*  SHAPE ADAPTER                                                     */
/* ------------------------------------------------------------------ */
/* The backend returns records already shaped like the code-managed    */
/* ones (see `_project_to_case_study` in server.py). Normalise a few   */
/* known differences so downstream code never needs to branch on       */
/* `source`.                                                            */
function normalizeAdminRecord(rec) {
  if (!rec) return null;
  return {
    source: "admin",
    status: rec.status || "draft",
    slug: rec.slug,
    projectName: rec.projectName,
    shortDescription: rec.shortDescription || "",
    heroSubtitle: rec.heroSubtitle || "",
    externalUrl: rec.externalUrl || null,
    heroImage: rec.heroImage || "",
    industry: rec.industry || null,
    industries: rec.industries || [],
    solutionType: rec.solutionType || null,
    platform: rec.platform || null,
    services: rec.services || [],
    markets: rec.markets || [],
    technologies: rec.technologies || [],
    cloudLabRole: rec.cloudLabRole || [],
    challenge: rec.challenge || "",
    solution: rec.solution || "",
    capabilities: rec.capabilities || [],
    workflow: rec.workflow || [],
    metrics: rec.metrics || [],          // already filtered to approved on the server
    testimonials: rec.testimonials || [],
    answerProfile: rec.answerProfile || {},
    gallery: rec.gallery || [],
    relatedProjects: rec.relatedProjects || [],
    seo: rec.seo || {},
  };
}

/* Code-managed records expose metrics via projectMetrics.js. Admin records
 * carry them inline on the record itself. Both branches end up returning
 * the same shape so MetricsBlock stays source-agnostic. */
export function getPublicMetricsForCaseStudy(cs) {
  if (!cs) return [];
  if (cs.source === "admin") {
    // Admin records: server has already filtered to approved_for_public_display=true
    return (cs.metrics || []).map((m) => ({
      id: m.id || `${cs.slug}-${(m.label || "metric").toLowerCase().replace(/\s+/g, "-")}`,
      label: m.label,
      value: m.value,
      before: m.before,
      after: m.after,
      qualifier: m.qualifier,
      context: m.context,
      kind: m.kind || "outcome",
    }));
  }
  // Code-managed — metricIds[] into projectMetrics.js
  return (cs.metricIds || [])
    .map((id) => getMetricById(id))
    .filter((m) => m && m.approvedForPublicDisplay);
}

/* ------------------------------------------------------------------ */
/*  HOOKS                                                             */
/* ------------------------------------------------------------------ */
export function usePublishedCaseStudies() {
  const codeManaged = getPublishedCaseStudies().map((c) => ({ ...c, source: "code" }));
  const [admin, setAdmin] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/case-studies")
      .then((r) => {
        if (!mounted) return;
        const list = Array.isArray(r.data) ? r.data : [];
        // Drop any admin record that collides with a code-managed slug.
        setAdmin(list
          .filter((rec) => !CODE_SLUGS.has(rec.slug))
          .map(normalizeAdminRecord)
          .filter(Boolean));
      })
      .catch(() => { if (mounted) setAdmin([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  return { items: [...codeManaged, ...admin], loading };
}

/* Single case study lookup — code wins on collision, then DB fallback. */
export function useCaseStudy(slug, { preview = false } = {}) {
  const code = getCodeManagedBySlug(slug);
  const codeMatch = code && code.status === "published";
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(!codeMatch);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (codeMatch) return;
    let mounted = true;
    const url = preview ? `/case-studies/${slug}?preview=1` : `/case-studies/${slug}`;
    api.get(url)
      .then((r) => { if (mounted) setAdmin(normalizeAdminRecord(r.data)); })
      .catch((e) => { if (mounted) setError(e); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug, preview, codeMatch]);

  if (codeMatch) return { data: { ...code, source: "code" }, loading: false, error: null };
  return { data: admin, loading, error };
}

/* Related — merged filter across code + admin. Used by RelatedCaseStudies. */
export function useRelatedCaseStudies({ service, industry, market, excludeSlug, limit = 3 } = {}) {
  const codeMatches = findCodeManaged({ service, market, industry })
    .filter((c) => c.slug !== excludeSlug)
    .map((c) => ({ ...c, source: "code" }));

  const [adminMatches, setAdminMatches] = useState([]);
  const [loading, setLoading] = useState(codeMatches.length < limit);

  useEffect(() => {
    if (codeMatches.length >= limit) {
      setAdminMatches([]);
      setLoading(false);
      return;
    }
    let mounted = true;
    api.get("/case-studies")
      .then((r) => {
        if (!mounted) return;
        const all = Array.isArray(r.data) ? r.data : [];
        const filtered = all
          .filter((rec) => rec.slug !== excludeSlug && !CODE_SLUGS.has(rec.slug))
          .filter((rec) => {
            if (service && !(rec.services || []).includes(service)) return false;
            if (industry && !(rec.industries || []).includes(industry) && rec.industry !== industry) return false;
            if (market && !(rec.markets || []).includes(market)) return false;
            return true;
          })
          .map(normalizeAdminRecord)
          .filter(Boolean);
        setAdminMatches(filtered);
      })
      .catch(() => { if (mounted) setAdminMatches([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, industry, market, excludeSlug, limit, codeMatches.length]);

  const items = [...codeMatches, ...adminMatches].slice(0, limit);
  return { items, loading };
}
